import {
  Component,
  ChangeDetectionStrategy,
  ElementRef,
  HostListener,
  ViewChild,
  inject,
  signal,
  computed,
  input,
} from '@angular/core';
import { Router } from '@angular/router';
import { ButtonComponent } from '../../../shared/components/wl_components/button/button.component';
import { ReactiveFormsModule } from '@angular/forms';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../../services/auth.service';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { LogOutModalComponent } from '../log-out-modal/log-out-modal.component';
import { accountButtonConfig, helpButton } from './header.constants';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'sjp-header',
  imports: [ButtonComponent, ReactiveFormsModule, MatIconModule],
  templateUrl: './header.html',
  styleUrl: './header.scss',
})
export class Header {
  isLoginView = input<boolean | undefined>(false);

  @ViewChild('accountMenuWrapper')
  private readonly _accountMenuWrapper?: ElementRef<HTMLDivElement>;

  private readonly _authService = inject(AuthService);
  private readonly _router = inject(Router);
  private readonly _dialog = inject(MatDialog);

  protected readonly isHidden = signal(true);
  
  protected readonly accountButtonConfig = computed(() => {
    const user = this._authService.currentUser();
    if (user && user.profile) {
      // Cognito custom claims (like 'username') might not be strongly typed in oidc-client-ts Profile interface
      const cognitoUsername = user.profile['username'] as string | undefined;
      const displayName = user.profile.name || user.profile.email || cognitoUsername || 'Current User';
      return { ...accountButtonConfig, text: displayName };
    }
    return accountButtonConfig;
  });
  
  protected readonly isAccountMenuOpen = signal(false);
  protected readonly accountMenuItems = [
    { label: 'Settings', icon: 'settings', action: () => this.onOpenSettings() },
    { label: 'Account', icon: 'person', action: () => this.onCloseAccountMenu() },
    {
      label: 'Log out',
      icon: 'logout',
      action: () => {
        this.onCloseAccountMenu();
        this.onLogOut();
      },
    },
  ];

  protected readonly helpButton = helpButton;
  protected readonly appVersion = `v${environment.appVersion}`;

  @HostListener('document:click', ['$event'])
  protected onDocumentClick(event: MouseEvent): void {
    if (!this.isAccountMenuOpen()) {
      return;
    }

    const target = event.target as Node | null;
    if (
      target &&
      this._accountMenuWrapper &&
      !this._accountMenuWrapper.nativeElement.contains(target)
    ) {
      this.isAccountMenuOpen.set(false);
    }
  }

  protected onToggleAccountMenu(): void {
    this.isAccountMenuOpen.set(!this.isAccountMenuOpen());
  }

  protected onOpenSettings(): void {
    this.isAccountMenuOpen.set(false);
    this._router.navigate(['/settings']);
  }

  protected onCloseAccountMenu(): void {
    this.isAccountMenuOpen.set(false);
  }

  setMenuWidth(triggerEl: HTMLElement): void {
    const triggerWidth = triggerEl.getBoundingClientRect().width;
    setTimeout(() => {
      // Find the currently open menu panel
      const panel = document.querySelector('.account-menu-panel') as HTMLElement;
      if (panel) {
        panel.style.width = `${triggerWidth}px`;
        panel.style.minWidth = `${triggerWidth}px`;
      }
    }, 0);
  }

  onLogOut(): void {
    const dialogRef = this._dialog.open(LogOutModalComponent, {
      width: '480px',
      maxWidth: '90vw',
    });

    dialogRef.afterClosed().subscribe((confirmLogout: boolean) => {
      if (confirmLogout) {
        this._router.navigate(['/log-out']);
      }
    });
  }
}
